/**
 * The timeline lights the canvas (DEC-269, 2026-10-11).
 *
 * Operator: "if a button is triggered in a timeline, the button on the
 * canvas shows the state. a quick flash for a momentary and on for a toggle".
 *
 * How a light gets here. `bridgeSync.ts` adds a `pwa` light cue beside each
 * timeline step that came from a canvas item; OPBridge plays the timeline and
 * pushes each light cue to every device of this user as a `pwa_action`
 * frame (`timeline_light`, args `{ itemId, mode, stepId }`), over the primary
 * socket or, for a timeline playing on another machine, a peer socket
 * (`opbridge/peerPwaSockets.ts`). This module turns those frames into
 * per-item lit state that the item components read.
 *
 * Honest limit (Phase A): a light means OPBridge DISPATCHED the step, not
 * that its MIDI reached the device. A missing port still lights; DEC-224's
 * launch warning already names that case.
 *
 * Phase B (2026-10-12, OPBridge S314): a bridge that advertises
 * `timeline_fired` gets no light cues. It sends a `timeline_fired` frame per
 * step it dispatched, AFTER the port write, with `ok` = that write; this
 * module maps the frame's `stepId` back to the step in the local timeline
 * library and lights its item only when `ok` is true, so a send that failed
 * never lights (DEC-124). Phase A stays for a bridge without the capability.
 *
 * Kept OUT of the components' own state on purpose:
 *   - only the active page's components are mounted, so a fire on another
 *     page has nowhere else to live (and lights that page's tab instead);
 *   - FireButton's `on()` feeds the Display mirror, the state label and the
 *     light-preset send decision, so ORing a timeline flash into it would
 *     leak. Components OR `timelineLit(id)` into their `data-on` ATTRIBUTE
 *     only.
 *
 * Toggles are not latched in Phase A: OPBridge's toggle engine never hears a
 * timeline's raw MIDI, so the toggle poll would put a latched light back to
 * off within 4 s. On a `toggle_set` bridge the step SETS the toggle
 * (`bridgeSync.ts`), so the toggle poll itself shows it on.
 */

import { createSignal, createRoot, createEffect, createMemo, on } from 'solid-js';
import { onPwaAction, onTimelineFired, type PwaActionEvent, type TimelineFiredEvent } from '../opbridge/consumerWs.ts';
import { bridges, primaryBridge } from '../opbridge/store.ts';
import { activeProject, activePage, type LayoutItem } from '../layout/projectStore.ts';
import { recordHelixCommand } from '../helix/lastCommanded.ts';
import { helixPresetName } from '../helix/library.ts';
import { TIMELINE_LIGHT_COMMAND, stepHoldsLight, type TimelineLightMode } from './bridgeSync.ts';
import { getTimeline } from './store.ts';
import type { Timeline } from '@opbuttons/schema';
import { timelineStateFor, watchTimelineState } from './statePoll.ts';

/** A flash stays lit this long: long enough to be seen, about a tap's flash. */
export const FLASH_MS = 140;
/** Flash safety. Two onsets on one item closer than this are faster than 3 a
 *  second (the DEC-262 ceiling, and the audience can see the screen,
 *  DEC-258): the item then HOLDS lit, shimmering slowly, instead of strobing. */
export const FAST_GAP_MS = 334;
/** A held-for-safety item lets go this long after its last onset. */
export const FAST_RELEASE_MS = 400;
/** The page tab of an off-page fire glows this long. */
export const PAGE_GLOW_MS = 700;

export interface TimelineLight {
  lit: boolean;
  /** Held lit by the flash-safety rule. */
  fast: boolean;
}

const [lights, setLights] = createSignal<Readonly<Record<string, TimelineLight>>>({});
const [glowing, setGlowing] = createSignal<Readonly<Record<string, true>>>({});

/** Is this item lit by a running timeline right now? */
export function timelineLit(itemId: string): boolean {
  return lights()[itemId]?.lit === true;
}

/** Is this item held lit by the flash-safety rule right now? */
export function timelineFast(itemId: string): boolean {
  return lights()[itemId]?.fast === true;
}

/** Is this page's tab glowing for a timeline fire on it? */
export function pageGlowing(pageId: string): boolean {
  return glowing()[pageId] === true;
}

const lastOnset = new Map<string, number>();
const flashTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** Items held on by an 'on' cue, with the bridge that sent it and when. */
const held = new Map<string, { bridgeId: string | undefined; at: number }>();
const glowTimers = new Map<string, ReturnType<typeof setTimeout>>();

function patch(itemId: string, next: Partial<TimelineLight>): void {
  setLights((prev) => {
    const cur = prev[itemId] ?? { lit: false, fast: false };
    const merged = { ...cur, ...next };
    if (prev[itemId] && merged.lit === cur.lit && merged.fast === cur.fast) return prev;
    return { ...prev, [itemId]: merged };
  });
}

function flash(itemId: string): void {
  const now = performance.now();
  const prevOnset = lastOnset.get(itemId);
  const tooFast = prevOnset !== undefined && now - prevOnset < FAST_GAP_MS;
  lastOnset.set(itemId, now);
  const fast = tooFast || timelineFast(itemId);
  const pending = flashTimers.get(itemId);
  if (pending !== undefined) clearTimeout(pending);
  patch(itemId, { lit: true, fast });
  flashTimers.set(itemId, setTimeout(() => {
    flashTimers.delete(itemId);
    patch(itemId, held.has(itemId) ? { fast: false } : { lit: false, fast: false });
  }, fast ? FAST_RELEASE_MS : FLASH_MS));
}

function holdOn(itemId: string, bridgeId: string | undefined): void {
  held.set(itemId, { bridgeId, at: performance.now() });
  patch(itemId, { lit: true });
}

function release(itemId: string): void {
  held.delete(itemId);
  if (!flashTimers.has(itemId)) patch(itemId, { lit: false, fast: false });
}

function findItem(itemId: string): { item: LayoutItem; pageId: string } | null {
  const proj = activeProject();
  if (!proj) return null;
  for (const pg of proj.pages) {
    const item = pg.items.find((i) => i.id === itemId);
    if (item) return { item, pageId: pg.id };
  }
  return null;
}

function glowPage(pageId: string): void {
  setGlowing((prev) => (prev[pageId] ? prev : { ...prev, [pageId]: true }));
  const t = glowTimers.get(pageId);
  if (t !== undefined) clearTimeout(t);
  glowTimers.set(pageId, setTimeout(() => {
    glowTimers.delete(pageId);
    setGlowing((prev) => {
      if (!prev[pageId]) return prev;
      const next = { ...prev };
      delete next[pageId];
      return next;
    });
  }, PAGE_GLOW_MS));
}

/** A timeline-fired preset or snapshot change is "last sent" too, so a
 *  Display bound to the pedal stops missing them. Same record the tiles
 *  write on a successful tap (PresetChange.tsx / SnapshotChange.tsx). */
function recordHelix(item: LayoutItem): void {
  if (item.kind === 'presetChange' && item.pedalId) {
    const presetName = item.name ?? helixPresetName(item.setlist, item.program) ?? item.presetName;
    recordHelixCommand(item.pedalId, {
      kind: 'preset',
      setlist: item.setlist,
      program: item.program,
      ...(presetName ? { presetName } : {}),
      ...(typeof item.snapshot === 'number' ? { snapshot: item.snapshot } : {}),
      at: Date.now(),
    });
  } else if (item.kind === 'snapshotChange' && item.pedalId) {
    recordHelixCommand(item.pedalId, { kind: 'snapshot', snapshot: item.snapshot, at: Date.now() });
  }
}

function parse(args: unknown): { itemId: string; mode: TimelineLightMode } | null {
  if (!args || typeof args !== 'object') return null;
  const a = args as Record<string, unknown>;
  const itemId = a['itemId'];
  const mode = a['mode'];
  if (typeof itemId !== 'string' || !itemId) return null;
  if (mode !== 'flash' && mode !== 'on' && mode !== 'off') return null;
  return { itemId, mode };
}

function applyLight(a: { itemId: string; mode: TimelineLightMode }, bridgeId: string | undefined): void {
  if (a.mode === 'off') { release(a.itemId); return; }
  if (a.mode === 'on') holdOn(a.itemId, bridgeId);
  else flash(a.itemId);
  const found = findItem(a.itemId);
  if (!found) return;
  if (found.pageId !== activePage()?.id) glowPage(found.pageId);
  recordHelix(found.item);
}

/** Exported for the smoke's pure checks; the app reaches it through the
 *  `pwa_action` listener only. */
export function handleTimelineLight(evt: PwaActionEvent): void {
  if (evt.command !== TIMELINE_LIGHT_COMMAND) return;
  const a = parse(evt.args);
  if (!a) return;
  applyLight(a, evt.bridgeId);
}

/** Which light a fired cue means (Phase B). The cue ids are ours
 *  (`bridgeSync.ts`): `<step>` is a step's first ON cue, `<step>-off` its
 *  first release, and `<step>-<n>` / `<step>-off-<n>` the extra actions of a
 *  compound step (a preset change's two messages), which light nothing so
 *  one step never flashes twice. Only `buttonRef` steps light. Pure:
 *  `itemOf` finds the canvas item, so the smoke can drive it offline. */
export function firedStepLight(
  t: Timeline | undefined, stepId: string, itemOf: (id: string) => LayoutItem | undefined,
): { itemId: string; mode: TimelineLightMode } | null {
  if (!t) return null;
  const refs = new Map<string, { buttonId: string; durationBars?: number | undefined }>();
  for (const lane of t.lanes) {
    for (const st of lane.steps) {
      if (st.kind === 'buttonRef') refs.set(st.id, st);
    }
  }
  const off = !refs.has(stepId) && stepId.endsWith('-off');
  const ref = refs.get(off ? stepId.slice(0, -4) : stepId);
  if (!ref) return null;
  const item = itemOf(ref.buttonId);
  if (!item) return null;
  if (off) return { itemId: item.id, mode: 'off' };
  return { itemId: item.id, mode: stepHoldsLight(item, ref) ? 'on' : 'flash' };
}

/** A `timeline_fired` frame. A failed send lights nothing; a release always
 *  lets go (an 'off' that failed must not leave a light stuck on). Exported
 *  for the smoke; the app reaches it through the listener only. */
export function handleTimelineFired(evt: TimelineFiredEvent): void {
  const a = firedStepLight(getTimeline(evt.timelineId), evt.stepId, (id) => findItem(id)?.item);
  if (!a) return;
  if (!evt.ok && a.mode !== 'off') return;
  applyLight(a, evt.bridgeId);
}

let started = false;

/** Listen for light cues, and let go of held lights once the bridge that
 *  lit them says nothing is playing (a song stopped mid-block never sends
 *  its 'off'). Idempotent. */
export function startCanvasLights(): void {
  if (started) return;
  started = true;
  onPwaAction(handleTimelineLight);
  onTimelineFired(handleTimelineFired);
  createRoot(() => {
    // Read every paired PEER's state too: a light held for a run on a peer is released when that peer stops. The
    // count-in asked the poll for this as a side effect until it moved onto the transport (2026-10-16), which broke the
    // release silently (`timeline_lights_the_canvas` [4]); this owner asks for it itself now.
    const unwatch = new Map<string, () => void>();
    const peerIds = createMemo(() => {
      const primaryId = primaryBridge()?.id;
      return bridges().filter((b) => !!b.bearer && b.id !== primaryId).map((b) => b.id).sort().join(' ');
    });
    createEffect(on(peerIds, (ids) => {
      const want = new Set(ids ? ids.split(' ') : []);
      for (const [id, release] of unwatch) if (!want.has(id)) { release(); unwatch.delete(id); }
      for (const id of want) if (!unwatch.has(id)) unwatch.set(id, watchTimelineState(id));
    }));
    createEffect(() => {
      // Subscribe to the poll UNCONDITIONALLY. `held` is a plain Map, so a
      // run that found it empty (every run at boot) would otherwise read no
      // signal at all and Solid would never run this effect again - measured
      // by `timeline_lights_the_canvas` [9] before this line existed.
      timelineStateFor('');
      const primaryId = primaryBridge()?.id;
      for (const [itemId, h] of [...held]) {
        if (!h.bridgeId) continue;
        const s = timelineStateFor(h.bridgeId === primaryId ? '' : h.bridgeId);
        // A reading taken BEFORE the light came on says nothing about it.
        if (!s || s.requestedAt < h.at) continue;
        if (!s.snap.songIsPlaying && (s.snap.activeActionIds?.length ?? 0) === 0) release(itemId);
      }
    });
  });
}
